import React, { useEffect } from 'react';
import { styled } from '@mui/system';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import {
  Check,
  AlertTriangle,
  Clock,
  TrendingUp,
  TrendingDown,
  BellRing,
  Lightbulb
} from 'lucide-react';
import { L, t } from '../strings';
import { motion, fadeUp, popIn, slideDown, ring, REDUCED } from './motion';

export const QUALITY_COLOR = { best: '#4f9a04', ok: '#d5921b', bad: '#b21625' };
export const QUALITY_BG = {
  best: 'rgba(121, 201, 18, 0.10)',
  ok: 'rgba(247, 213, 62, 0.16)',
  bad: 'rgba(211, 47, 47, 0.08)'
};

const QUALITY_BORDER = {
  best: 'rgba(121, 201, 18, 0.35)',
  ok: 'rgba(213, 146, 27, 0.30)',
  bad: 'rgba(178, 22, 37, 0.22)'
};

export const Card = styled('section')(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  border: `1px solid ${theme.palette.grey[100]}`,
  borderRadius: 15,
  padding: '28px 32px',
  marginBottom: 24,
  ...motion(`${fadeUp} 380ms ease-out both`),
  [theme.breakpoints.down('sm')]: { padding: '18px 16px' }
}));

export const CardTitle = styled(Typography)(() => ({
  fontFamily: 'Unbounded',
  fontSize: 20,
  fontWeight: 600,
  lineHeight: '28px',
  marginBottom: 12
}));

export const Body = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  lineHeight: '24px',
  color: theme.palette.textPrimary.main
}));

export const Muted = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  lineHeight: '20px',
  color: theme.palette.textTertiary.main
}));

export const PrimaryButton = (props) => <Button variant="contained" color="primary" {...props} />;
export const SecondaryButton = (props) => <Button variant="outlined" color="primary" {...props} />;

const AvatarCircle = styled('div')(({ color, size, ring }) => ({
  position: 'relative',
  width: size,
  height: size,
  minWidth: size,
  borderRadius: '50%',
  backgroundColor: color,
  backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.22), rgba(0,0,0,0.10))',
  boxShadow: ring ? '0 0 0 2px #fff' : 'none',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'Rubik-Regular',
  fontWeight: 500,
  fontSize: Math.round(size * 0.36),
  letterSpacing: 0.5
}));

const OnlineDot = styled('span')(({ size }) => ({
  position: 'absolute',
  right: -1,
  bottom: -1,
  width: Math.max(9, Math.round(size * 0.28)),
  height: Math.max(9, Math.round(size * 0.28)),
  borderRadius: '50%',
  backgroundColor: '#79c912',
  border: '2px solid #fff',
  boxSizing: 'border-box'
}));

export const Avatar = ({ person, size = 40, online = false, ring = false, style }) => (
  <AvatarCircle
    color={person?.color || '#828282'}
    size={size}
    ring={ring}
    style={style}
    aria-hidden="true">
    {person?.initials || '?'}
    {online && <OnlineDot size={size} />}
  </AvatarCircle>
);

const OptionButton = styled('button')(({ theme, state, delay = 0 }) => {
  const base = {
    width: '100%',
    textAlign: 'left',
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    padding: '14px 16px',
    borderRadius: 12,
    border: '2px solid #e0e0e0',
    backgroundColor: '#fff',
    fontFamily: 'Rubik-Regular',
    fontSize: 16,
    lineHeight: '24px',
    color: theme.palette.textPrimary.main,
    cursor: 'pointer',
    transition:
      'border-color 160ms ease, background-color 160ms ease, transform 160ms ease, box-shadow 160ms ease, opacity 240ms ease',
    ...motion(`${fadeUp} 360ms ease-out ${delay}ms both`),
    '&:hover': {
      borderColor: theme.palette.primary.main,
      transform: 'translateY(-1px)',
      boxShadow: '0 6px 16px rgba(34, 34, 34, 0.06)'
    },
    '&:hover .opt-index': { backgroundColor: theme.palette.primary.salad },
    '&:active': { transform: 'translateY(0) scale(0.995)' },
    '&:focus-visible': { outline: `3px solid ${theme.palette.primary.light}`, outlineOffset: 1 },
    '&:disabled': { cursor: 'default', transform: 'none', boxShadow: 'none' }
  };
  if (state === 'picked')
    return {
      ...base,
      borderColor: theme.palette.primary.main,
      backgroundColor: 'rgba(121, 201, 18, 0.06)',
      ...motion(`${popIn} 320ms ease-out both`),
      '& .opt-index': { backgroundColor: theme.palette.primary.salad }
    };
  if (state === 'best')
    return {
      ...base,
      borderColor: QUALITY_COLOR.best,
      borderStyle: 'dashed',
      animation: 'none'
    };
  if (state === 'dim')
    return { ...base, opacity: 0.5, animation: 'none', '&:hover': {}, '&:disabled': {} };
  return base;
});

const OptionIndex = styled('span')(({ theme }) => ({
  width: 26,
  height: 26,
  minWidth: 26,
  borderRadius: 8,
  backgroundColor: theme.palette.grey[100],
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 13,
  fontWeight: 500,
  marginTop: 1,
  transition: 'background-color 160ms ease'
}));

const Tag = styled('span')(({ color }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '3px 10px',
  borderRadius: 100,
  border: `1px solid ${color}`,
  color,
  fontSize: 12,
  fontWeight: 500,
  fontFamily: 'Rubik-Regular',
  whiteSpace: 'nowrap',
  marginLeft: 'auto',
  ...motion(`${popIn} 300ms ease-out 120ms both`)
}));

export const FeedbackBox = styled('div')(({ q, delay = 0 }) => ({
  marginTop: 12,
  padding: '14px 16px',
  borderRadius: 12,
  backgroundColor: QUALITY_BG[q] || '#f5f5f5',
  border: `1px solid ${QUALITY_BORDER[q] || '#ebedee'}`,
  ...motion(`${fadeUp} 360ms ease-out ${delay}ms both`)
}));

const FeedbackLabel = styled('div')(({ q }) => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 13,
  fontWeight: 500,
  color: QUALITY_COLOR[q],
  marginBottom: 4
}));

const FxChips = styled('span')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginTop: 10
}));

const FxChip = styled('span')(({ positive, delay }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 9px 3px 7px',
  borderRadius: 100,
  fontFamily: 'Rubik-Regular',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  color: positive ? QUALITY_COLOR.best : QUALITY_COLOR.bad,
  backgroundColor: positive ? 'rgba(121, 201, 18, 0.16)' : 'rgba(211, 47, 47, 0.10)',
  ...motion(`${popIn} 320ms ease-out ${delay}ms both`)
}));

export const FxLine = ({ fx, kpiLabels }) => {
  if (!fx) return null;
  const parts = Object.entries(fx).filter(([, d]) => d);
  if (!parts.length) return null;
  return (
    <FxChips>
      {parts.map(([k, d], i) => (
        <FxChip key={k} positive={d > 0} delay={250 + i * 90}>
          {d > 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
          {kpiLabels[k] || k} {d > 0 ? '+' : ''}
          {d}
        </FxChip>
      ))}
    </FxChips>
  );
};

// Number keys 1..n pick an option while nothing is picked yet.
export const useNumberKeys = (count, onKey, enabled = true) => {
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      const n = Number(e.key);
      if (Number.isInteger(n) && n >= 1 && n <= count) onKey(n - 1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [count, onKey, enabled]);
};

// Feedback for a picked option: why it worked or not, its KPI effect, and the
// stronger move when there was one. Shared by OptionList and the messenger.
export const OptionFeedback = ({ options, picked, dilemma, kpiLabels, principle, delay = 0 }) => {
  const o = options[picked];
  const others = options
    .map((x, i) => ({ ...x, i }))
    .filter((x) => x.q === 'best' && x.i !== picked && (o.q !== 'best' || dilemma));
  return (
    <div>
      <FeedbackBox q={o.q} delay={delay}>
        <FeedbackLabel q={o.q}>
          {o.q === 'best' ? (
            <Check size={14} style={{ verticalAlign: -2 }} />
          ) : (
            <AlertTriangle size={14} style={{ verticalAlign: -2 }} />
          )}{' '}
          {t('yourChoice')}
        </FeedbackLabel>
        <Body>{L(o.fb)}</Body>
        <FxLine fx={o.fx} kpiLabels={kpiLabels} />
        {o.later && (
          <Muted style={{ marginTop: 8 }}>
            <Clock size={13} style={{ verticalAlign: -2 }} /> {t('later')}…
          </Muted>
        )}
      </FeedbackBox>
      {others.map((b, n) => (
        <FeedbackBox key={b.i} q="best" delay={delay + 180 + n * 120}>
          <FeedbackLabel q="best">
            {dilemma && o.q === 'best' ? t('alsoStrong') : t('strongerMove')}
          </FeedbackLabel>
          <Body style={{ fontWeight: 500, marginBottom: 6 }}>«{L(b.text)}»</Body>
          <Body>{L(b.fb)}</Body>
        </FeedbackBox>
      ))}
      {principle && <Principle principle={principle} delay={delay + 300} />}
    </div>
  );
};

const PrincipleBox = styled('div')(({ theme, delay }) => ({
  display: 'flex',
  gap: 10,
  alignItems: 'flex-start',
  marginTop: 14,
  padding: '12px 14px',
  borderRadius: 12,
  border: `1px dashed ${theme.palette.primary.main}`,
  backgroundColor: theme.palette.primary.pale,
  fontFamily: 'Rubik-Regular',
  fontSize: 15,
  lineHeight: '22px',
  color: theme.palette.textPrimary.main,
  ...motion(`${fadeUp} 360ms ease-out ${delay}ms both`),
  '& svg': { color: '#4f9a04', flexShrink: 0, marginTop: 2 }
}));

export const Principle = ({ principle, delay = 0 }) => (
  <PrincipleBox delay={delay}>
    <Lightbulb size={18} />
    <span>
      <b style={{ fontWeight: 500 }}>{t('principle')}:</b> {L(principle)}
    </span>
  </PrincipleBox>
);

// Option cards for chat/choice/queue steps. Once `picked` is set the list
// reveals the feedback for the picked option and marks the stronger move.
export const OptionList = ({
  options,
  order,
  picked,
  dilemma,
  onPick,
  kpiLabels,
  principle,
  keys = true
}) => {
  const revealed = picked != null;
  const pickedQ = revealed ? options[picked].q : null;
  useNumberKeys(order.length, (pos) => onPick(order[pos]), keys && !revealed);
  return (
    <div>
      {order.map((idx, pos) => {
        const o = options[idx];
        let state = 'idle';
        const otherBest = o.q === 'best' && idx !== picked && (pickedQ !== 'best' || dilemma);
        if (revealed) {
          if (idx === picked) state = 'picked';
          else if (otherBest) state = 'best';
          else state = 'dim';
        }
        const showFb = revealed && (idx === picked || otherBest);
        return (
          <div key={idx} style={{ marginBottom: 10 }}>
            <OptionButton
              type="button"
              state={state}
              delay={pos * 70}
              disabled={revealed}
              onClick={() => onPick(idx)}>
              <OptionIndex className="opt-index">{pos + 1}</OptionIndex>
              <span style={{ flex: 1 }}>{L(o.text)}</span>
              {revealed && idx === picked && (
                <Tag color={QUALITY_COLOR[o.q]}>{t('yourChoice')}</Tag>
              )}
              {revealed && idx !== picked && state === 'best' && (
                <Tag color={QUALITY_COLOR.best}>
                  {dilemma && pickedQ === 'best' ? t('alsoStrong') : t('strongerMove')}
                </Tag>
              )}
            </OptionButton>
            {showFb && (
              <FeedbackBox q={o.q} delay={idx === picked ? 120 : 260}>
                {idx === picked ? (
                  <FeedbackLabel q={o.q}>
                    {o.q === 'best' ? (
                      <Check size={14} style={{ verticalAlign: -2 }} />
                    ) : (
                      <AlertTriangle size={14} style={{ verticalAlign: -2 }} />
                    )}{' '}
                    {t('yourChoice')}
                  </FeedbackLabel>
                ) : (
                  <FeedbackLabel q="best">
                    {dilemma && pickedQ === 'best' ? t('alsoStrong') : t('strongerMove')}
                  </FeedbackLabel>
                )}
                <Body>{L(o.fb)}</Body>
                {idx === picked && <FxLine fx={o.fx} kpiLabels={kpiLabels} />}
                {idx === picked && o.later && (
                  <Muted style={{ marginTop: 8 }}>
                    <Clock size={13} style={{ verticalAlign: -2 }} /> {t('later')}…
                  </Muted>
                )}
              </FeedbackBox>
            )}
          </div>
        );
      })}
      {revealed && principle && <Principle principle={principle} delay={400} />}
      {!revealed && keys && <KeyHint count={order.length} />}
    </div>
  );
};

const KeyHintText = styled('div')(({ theme }) => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 12,
  color: theme.palette.textDisabled.main,
  marginTop: 4,
  [theme.breakpoints.down('md')]: { display: 'none' },
  '& kbd': {
    display: 'inline-block',
    padding: '0 5px',
    borderRadius: 4,
    border: `1px solid ${theme.palette.grey[100]}`,
    borderBottomWidth: 2,
    fontFamily: 'inherit',
    fontSize: 11,
    color: theme.palette.textTertiary.main
  }
}));

export const KeyHint = ({ count }) => (
  <KeyHintText>
    {t('keysHint')} <kbd>1</kbd>–<kbd>{count}</kbd>
  </KeyHintText>
);

const Reply = styled('button')(({ theme, delay }) => ({
  width: '100%',
  display: 'flex',
  gap: 12,
  alignItems: 'flex-start',
  textAlign: 'left',
  padding: '12px 14px',
  marginBottom: 8,
  borderRadius: 14,
  border: `1.5px solid ${theme.palette.grey[100]}`,
  backgroundColor: '#fafbf8',
  fontFamily: 'Rubik-Regular',
  fontSize: 15,
  lineHeight: '22px',
  color: theme.palette.textPrimary.main,
  cursor: 'pointer',
  transition:
    'border-color 160ms ease, background-color 160ms ease, transform 160ms ease, box-shadow 160ms ease',
  ...motion(`${fadeUp} 340ms ease-out ${delay}ms both`),
  '& .send': { opacity: 0, transform: 'translateX(-4px)', transition: 'all 160ms ease' },
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: '#fff',
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 16px rgba(34, 34, 34, 0.06)'
  },
  '&:hover .send': { opacity: 1, transform: 'translateX(0)' },
  '&:hover .opt-index': { backgroundColor: theme.palette.primary.salad },
  '&:focus-visible': { outline: `3px solid ${theme.palette.primary.light}`, outlineOffset: 1 }
}));

const ReplyIndex = styled('span')(({ theme }) => ({
  width: 24,
  height: 24,
  minWidth: 24,
  borderRadius: 7,
  backgroundColor: theme.palette.grey[100],
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 500,
  marginTop: -1,
  transition: 'background-color 160ms ease'
}));

// Reply cards for conversation steps (messenger, phone call). Number keys pick.
export const ReplyOptions = ({ options, order, onPick, icon: Icon }) => {
  useNumberKeys(order.length, (pos) => onPick(order[pos]));
  return (
    <>
      {order.map((idx, pos) => (
        <Reply key={idx} type="button" delay={80 + pos * 70} onClick={() => onPick(idx)}>
          <ReplyIndex className="opt-index">{pos + 1}</ReplyIndex>
          <span style={{ flex: 1 }}>{L(options[idx].text)}</span>
          {Icon && <Icon className="send" size={16} color="#4f9a04" style={{ marginTop: 3 }} />}
        </Reply>
      ))}
      <KeyHint count={order.length} />
    </>
  );
};

export const ExplainBox = ({ ok, children, label }) => (
  <FeedbackBox q={ok === null ? 'ok' : ok ? 'best' : 'bad'}>
    <FeedbackLabel q={ok === null ? 'ok' : ok ? 'best' : 'bad'}>
      {label || t('explanation')}
    </FeedbackLabel>
    <Body>{children}</Body>
  </FeedbackBox>
);

const NoticeBox = styled('div')(({ theme, delay }) => ({
  display: 'flex',
  gap: 14,
  alignItems: 'flex-start',
  padding: '14px 16px',
  borderRadius: 14,
  border: `1px solid ${theme.palette.secondary.main}`,
  backgroundColor: '#fffbea',
  boxShadow: '0 8px 24px rgba(213, 178, 22, 0.14)',
  marginBottom: 16,
  ...motion(`${slideDown} 420ms cubic-bezier(.2,.9,.3,1.2) ${delay}ms both`)
}));

const NoticeIcon = styled('div')(({ theme, delay }) => ({
  width: 36,
  height: 36,
  minWidth: 36,
  borderRadius: 10,
  backgroundColor: theme.palette.secondary.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    transformOrigin: '50% 10%',
    ...motion(`${ring} 900ms ease-in-out ${delay + 380}ms 1`)
  },
  [REDUCED]: { '& svg': { animation: 'none' } }
}));

export const Notices = ({ notices }) => {
  if (!notices?.length) return null;
  return (
    <div>
      {notices.map((n, i) => (
        <NoticeBox key={i} delay={i * 150} role="status">
          <NoticeIcon delay={i * 150}>
            <BellRing size={18} />
          </NoticeIcon>
          <div>
            <Muted style={{ marginBottom: 2 }}>{t('consequence')}</Muted>
            <Body>{L(n.note)}</Body>
          </div>
        </NoticeBox>
      ))}
    </div>
  );
};

export const Pill = styled('span')(({ theme, color }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 12px',
  borderRadius: 100,
  border: `1px solid ${color || theme.palette.primary.salad}`,
  backgroundColor: '#fff',
  fontSize: 12,
  fontWeight: 500,
  fontFamily: 'Rubik-Regular',
  color: theme.palette.textPrimary.main,
  whiteSpace: 'nowrap'
}));

export const shuffledOrder = (n, seed) => {
  // Deterministic per step so re-renders keep the same order.
  const arr = Array.from({ length: n }, (_, i) => i);
  let s = seed;
  for (let i = n - 1; i > 0; i -= 1) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

export const seedFrom = (str) =>
  Array.from(String(str)).reduce((a, c) => (a * 31 + c.charCodeAt(0)) % 100000, 7);
